import { TEXT_FONT_SIZES, NOTE_COLORS, colors, shapeTools, GRID_SIZES, GRID_SIZE_LABELS, GridMode, CANVAS_COLORS } from './types';
import { useDrawingBoard } from './useDrawingBoard';
import { isFillableTool } from './utils';

export type DrawingBoardState = ReturnType<typeof useDrawingBoard>;

export default function DrawingToolbar({ board }: { board: DrawingBoardState }) {
  const {
    tool, setTool, isFullscreen, setIsFullscreen, noteColorIndex, setNoteColorIndex,
    textFontSize, setTextFontSize, fillEnabled, setFillEnabled, brushSize, setBrushSize,
    brushOpacity, setBrushOpacity, brushColor, setBrushColor, stylusMode, pressureEnabled,
    setPressureEnabled, gridMode, setGridMode, gridSize, setGridSize,
    canvasColor, setCanvasColor, undo, redo,
    historyIndex, history, clearCanvas
  } = board;

  return (
          <div className={`flex flex-wrap items-center gap-3 ${isFullscreen ? 'px-4 py-2 bg-stone-800 border-b border-stone-700 flex-shrink-0' : ''}`}>
            {/* Tool Toggle */}
            <div className={`flex items-center rounded-lg p-1 ${isFullscreen ? 'bg-stone-700' : 'bg-stone-100'}`}>
              <button
                onClick={() => setTool('pen')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                  tool === 'pen'
                    ? (isFullscreen ? 'bg-stone-600 text-white shadow-sm' : 'bg-white text-stone-800 shadow-sm')
                    : (isFullscreen ? 'text-stone-400' : 'text-stone-500')
                }`}
              >
                <i className="ri-pencil-line mr-1"></i>
                Kalem
              </button>
              <button
                onClick={() => setTool('eraser')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                  tool === 'eraser'
                    ? (isFullscreen ? 'bg-stone-600 text-white shadow-sm' : 'bg-white text-stone-800 shadow-sm')
                    : (isFullscreen ? 'text-stone-400' : 'text-stone-500')
                }`}
              >
                <i className="ri-eraser-line mr-1"></i>
                Silgi
              </button>
            </div>

            {/* Shape Tools */}
            <div className={`flex items-center rounded-lg p-1 ${isFullscreen ? 'bg-stone-700' : 'bg-stone-100'}`}>
              {shapeTools.map((shape) => (
                <button
                  key={shape.type}
                  onClick={() => setTool(shape.type)}
                  title={shape.label}
                  className={`w-8 h-8 flex items-center justify-center rounded-md text-sm transition-colors cursor-pointer ${
                    tool === shape.type
                      ? (isFullscreen ? 'bg-stone-600 text-white shadow-sm' : 'bg-white text-stone-800 shadow-sm')
                      : (isFullscreen ? 'text-stone-400' : 'text-stone-500')
                  }`}
                >
                  <i className={shape.icon}></i>
                </button>
              ))}
              {/* Fill Tool (Bucket) */}
              <button
                onClick={() => setTool('fill')}
                title="Boya Kovası - Şekli Doldur"
                className={`w-8 h-8 flex items-center justify-center rounded-md text-sm transition-colors cursor-pointer ${
                  tool === 'fill'
                    ? (isFullscreen ? 'bg-stone-600 text-white shadow-sm' : 'bg-white text-stone-800 shadow-sm')
                    : (isFullscreen ? 'text-stone-400' : 'text-stone-500')
                }`}
              >
                <i className="ri-paint-fill"></i>
              </button>
              {/* Note Tool */}
              <button
                onClick={() => setTool('note')}
                title="Post-it Not Ekle"
                className={`w-8 h-8 flex items-center justify-center rounded-md text-sm transition-colors cursor-pointer ${
                  tool === 'note'
                    ? (isFullscreen ? 'bg-stone-600 text-white shadow-sm' : 'bg-white text-stone-800 shadow-sm')
                    : (isFullscreen ? 'text-stone-400' : 'text-stone-500')
                }`}
              >
                <i className="ri-sticky-note-line"></i>
              </button>
              {/* Text Tool */}
              <button
                onClick={() => setTool('text')}
                title="Metin Ekle"
                className={`w-8 h-8 flex items-center justify-center rounded-md text-sm transition-colors cursor-pointer ${
                  tool === 'text'
                    ? (isFullscreen ? 'bg-stone-600 text-white shadow-sm' : 'bg-white text-stone-800 shadow-sm')
                    : (isFullscreen ? 'text-stone-400' : 'text-stone-500')
                }`}
              >
                <i className="ri-text"></i>
              </button>
            </div>

            {/* Note Color Picker (only when note tool active) */}
            {tool === 'note' && (
              <div className={`flex items-center gap-1.5 px-2 py-1 rounded-lg ${
                isFullscreen ? 'bg-stone-700' : 'bg-stone-100'
              }`}>
                <span className={`text-xs mr-1 ${ isFullscreen ? 'text-stone-400' : 'text-stone-500'}`}>Not rengi:</span>
                {NOTE_COLORS.map((nc, idx) => (
                  <button
                    key={idx}
                    onClick={() => setNoteColorIndex(idx)}
                    title={nc.label}
                    className={`w-5 h-5 rounded-full border-2 transition-all cursor-pointer ${
                      noteColorIndex === idx ? 'scale-125 border-stone-700' : 'border-transparent'
                    }`}
                    style={{ backgroundColor: nc.bg }}
                  />
                ))}
              </div>
            )}

            {/* Text Font Size (only when text tool active) */}
            {tool === 'text' && (
              <div className={`flex items-center gap-1.5 px-2 py-1 rounded-lg ${
                isFullscreen ? 'bg-stone-700' : 'bg-stone-100'
              }`}>
                <span className={`text-xs mr-1 ${ isFullscreen ? 'text-stone-400' : 'text-stone-500'}`}>Boyut:</span>
                {TEXT_FONT_SIZES.map((size) => (
                  <button
                    key={size}
                    onClick={() => setTextFontSize(size)}
                    className={`px-1.5 py-0.5 rounded text-xs font-medium transition-colors cursor-pointer ${
                      textFontSize === size
                        ? (isFullscreen ? 'bg-stone-600 text-white' : 'bg-white text-stone-800 shadow-sm')
                        : (isFullscreen ? 'text-stone-400 hover:text-stone-200' : 'text-stone-500 hover:text-stone-700')
                    }`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            )}

            {/* Fill Toggle (only when a shape tool is active) */}
            {isFillableTool(tool) && (
              <button
                onClick={() => setFillEnabled(!fillEnabled)}
                className={`flex items-center gap-1 px-2 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer ${
                  fillEnabled
                    ? (isFullscreen ? 'bg-stone-600 text-amber-400' : 'bg-white text-amber-700 shadow-sm')
                    : (isFullscreen ? 'text-stone-400' : 'text-stone-500')
                }`}
                title="Şekli Doldur"
              >
                <i className="ri-paint-fill"></i>
                Doldur
              </button>
            )}

            {/* Brush Size */}
            <div className="flex items-center gap-2">
              <span className={`text-xs ${isFullscreen ? 'text-stone-400' : 'text-stone-500'}`}>Kalınlık:</span>
              <input
                type="range"
                min="1"
                max="20"
                value={brushSize}
                onChange={(e) => setBrushSize(Number(e.target.value))}
                className={`w-24 ${isFullscreen ? 'accent-amber-500' : 'accent-stone-800'}`}
              />
              <span className={`text-xs w-4 ${isFullscreen ? 'text-stone-400' : 'text-stone-500'}`}>{brushSize}</span>
            </div>

            {/* Opacity */}
            <div className="flex items-center gap-2">
              <span className={`text-xs ${isFullscreen ? 'text-stone-400' : 'text-stone-500'}`}>Opasite:</span>
              <input
                type="range"
                min="5"
                max="100"
                value={Math.round(brushOpacity * 100)}
                onChange={(e) => setBrushOpacity(Number(e.target.value) / 100)}
                className={`w-20 ${isFullscreen ? 'accent-amber-500' : 'accent-stone-800'}`}
              />
              <span className={`text-xs w-6 text-right ${isFullscreen ? 'text-stone-400' : 'text-stone-500'}`}>
                {Math.round(brushOpacity * 100)}%
              </span>
            </div>

            {/* Colors */}
            <div className="flex items-center gap-1">
              {colors
                .filter(color => color !== '#ffffff' || canvasColor === 'black')
                .map((color) => (
                  <button
                    key={color}
                    onClick={() => { setBrushColor(color); if (tool === 'eraser') setTool('pen'); }}
                    className={`w-6 h-6 rounded-full border-2 transition-all cursor-pointer ${
                      brushColor === color && tool !== 'eraser' ? 'border-stone-800 scale-110' : 'border-transparent'
                    } ${color === '#ffffff' ? 'border-stone-200' : ''}`}
                    style={{ backgroundColor: color }}
                  />
                ))}
            </div>

            {/* Canvas Background Color */}
            <div className={`flex items-center gap-1.5 px-2 py-1 rounded-lg ${
              isFullscreen ? 'bg-stone-700' : 'bg-stone-100'
            }`}>
              <span className={`text-xs mr-1 ${ isFullscreen ? 'text-stone-400' : 'text-stone-500'}`}>Kağıt:</span>
              {CANVAS_COLORS.map((cc) => (
                <button
                  key={cc.id}
                  onClick={() => setCanvasColor(cc.id)}
                  title={`Kağıt Rengi: ${cc.label}`}
                  className={`w-5 h-5 rounded-md border-2 transition-all cursor-pointer ${
                    canvasColor === cc.id ? 'scale-110 border-stone-800' : 'border-transparent'
                  }`}
                  style={{ backgroundColor: cc.bg }}
                />
              ))}
            </div>

            <div className="flex-1"></div>

            {/* Stylus Mode Indicator */}
            {stylusMode && (
              <div className={`flex items-center gap-2 px-2 py-1 rounded-lg ${isFullscreen ? 'bg-stone-700 border border-stone-600' : 'bg-amber-50 border border-amber-200'}`}>
                <i className={`ri-pen-nib-line text-xs ${isFullscreen ? 'text-amber-400' : 'text-amber-600'}`}></i>
                <span className={`text-xs font-medium ${isFullscreen ? 'text-amber-400' : 'text-amber-700'}`}>Kalem Modu</span>
                <button
                  onClick={() => setPressureEnabled(!pressureEnabled)}
                  className={`text-xs px-1.5 py-0.5 rounded transition-colors cursor-pointer ${
                    pressureEnabled
                      ? (isFullscreen ? 'bg-stone-600 text-amber-400' : 'bg-amber-200 text-amber-800')
                      : (isFullscreen ? 'text-stone-500' : 'text-amber-500')
                  }`}
                  title="Basınç Hassasiyeti"
                >
                  {pressureEnabled ? 'Basınç: Açık' : 'Basınç: Kapalı'}
                </button>
              </div>
            )}

            {/* Grid Controls */}
            <div className={`flex items-center gap-1 px-1.5 py-1 rounded-lg ${
              isFullscreen ? 'bg-stone-700' : 'bg-stone-100'
            }`}>
              <span className={`text-xs mr-0.5 ${isFullscreen ? 'text-stone-400' : 'text-stone-500'}`}>
                <i className="ri-grid-line"></i>
              </span>
              {(['none', 'lines', 'dots', 'cross'] as GridMode[]).map((mode) => {
                const icons: Record<GridMode, string> = {
                  none: 'ri-close-line',
                  lines: 'ri-grid-line',
                  dots: 'ri-more-2-fill',
                  cross: 'ri-add-line',
                };
                const labels: Record<GridMode, string> = {
                  none: 'Izgara Yok',
                  lines: 'Çizgi Izgara',
                  dots: 'Nokta Izgara',
                  cross: 'Artı Izgara',
                };
                return (
                  <button
                    key={mode}
                    onClick={() => setGridMode(mode)}
                    title={labels[mode]}
                    className={`w-7 h-7 flex items-center justify-center rounded-md text-xs transition-colors cursor-pointer ${
                      gridMode === mode
                        ? (isFullscreen ? 'bg-stone-600 text-white' : 'bg-white text-stone-800 shadow-sm')
                        : (isFullscreen ? 'text-stone-400 hover:text-stone-200' : 'text-stone-500 hover:text-stone-700')
                    }`}
                  >
                    <i className={icons[mode]}></i>
                  </button>
                );
              })}
              {gridMode !== 'none' && (
                <div className={`flex items-center gap-0.5 ml-1 pl-1.5 border-l ${
                  isFullscreen ? 'border-stone-600' : 'border-stone-300'
                }`}>
                  {GRID_SIZES.map((size, idx) => (
                    <button
                      key={size}
                      onClick={() => setGridSize(size)}
                      title={`Izgara boyutu: ${size}px`}
                      className={`px-1.5 py-0.5 rounded text-xs font-medium transition-colors cursor-pointer ${
                        gridSize === size
                          ? (isFullscreen ? 'bg-stone-600 text-white' : 'bg-white text-stone-800 shadow-sm')
                          : (isFullscreen ? 'text-stone-400 hover:text-stone-200' : 'text-stone-500 hover:text-stone-700')
                      }`}
                    >
                      {GRID_SIZE_LABELS[idx]}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Undo/Redo */}
            <div className="flex items-center gap-1">
              <button
                onClick={undo}
                disabled={historyIndex < 0}
                className={`px-2 py-1.5 text-sm rounded-lg transition-colors cursor-pointer disabled:opacity-30 ${
                  isFullscreen ? 'text-stone-400 hover:bg-stone-700' : 'text-stone-600 hover:bg-stone-100'
                }`}
                title="Geri Al"
              >
                <i className="ri-arrow-go-back-line"></i>
              </button>
              <button
                onClick={redo}
                disabled={historyIndex >= history.length - 1}
                className={`px-2 py-1.5 text-sm rounded-lg transition-colors cursor-pointer disabled:opacity-30 ${
                  isFullscreen ? 'text-stone-400 hover:bg-stone-700' : 'text-stone-600 hover:bg-stone-100'
                }`}
                title="İleri Al"
              >
                <i className="ri-arrow-go-forward-line"></i>
              </button>
            </div>

            <button
              onClick={clearCanvas}
              className={`px-3 py-1.5 text-sm rounded-lg transition-colors cursor-pointer ${
                isFullscreen ? 'text-red-400 hover:bg-stone-700' : 'text-red-600 hover:bg-red-50'
              }`}
            >
              <i className="ri-delete-bin-line mr-1"></i>
              Temizle
            </button>

            {/* Fullscreen Toggle (inline mode only) */}
            {!isFullscreen && (
              <button
                onClick={() => setIsFullscreen(true)}
                className="px-3 py-1.5 text-sm text-stone-600 hover:bg-stone-100 rounded-lg transition-colors cursor-pointer flex items-center gap-1"
                title="Tam Ekran"
              >
                <i className="ri-fullscreen-line"></i>
                Tam Ekran
              </button>
            )}
          </div>

  );
}
